import {
  type RefCallback,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'

import classnames from 'classnames'
import { motion, useReducedMotion } from 'framer-motion'

type ElementState = 'active' | 'focus' | 'hover' | 'initial'

export interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  textSize?: 'small' | 'medium'
}

function useElementState(): [RefCallback<HTMLElement>, ElementState] {
  const ref = useRef<HTMLElement | null>(null)
  const [state, setState] = useState({
    focus: false,
    hover: false,
    active: false
  })

  const setRef: RefCallback<HTMLElement> = useCallback(element => {
    ref.current = element
  }, [])

  useEffect(() => {
    const element = ref.current

    if (!element) {
      return
    }

    const pointerenter = () =>
      setState(current => ({ ...current, hover: true }))

    const pointerleave = () =>
      setState(current => ({ ...current, hover: false }))

    const focus = () => setState(current => ({ ...current, focus: true }))
    const blur = () => setState(current => ({ ...current, focus: false }))
    const pointerdown = () => {
      setState(current => ({ ...current, active: true }))

      const pointerup = () => {
        setState(current => ({ ...current, active: false }))
        window.removeEventListener('pointerup', pointerup)
        window.removeEventListener('pointercancel', pointerup)
      }

      window.addEventListener('pointerup', pointerup)
      window.addEventListener('pointercancel', pointerup)
    }

    const keydown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter') {
        return
      }

      setState(current => ({ ...current, active: true }))

      const keyup = () => setState(current => ({ ...current, active: false }))

      window.addEventListener('keyup', keyup, { once: true })
    }

    element.addEventListener('pointerenter', pointerenter)
    element.addEventListener('pointerleave', pointerleave)
    element.addEventListener('focus', focus)
    element.addEventListener('blur', blur)
    element.addEventListener('pointerdown', pointerdown)
    element.addEventListener('keydown', keydown)

    return () => {
      element.removeEventListener('pointerenter', pointerenter)
      element.removeEventListener('pointerleave', pointerleave)
      element.removeEventListener('focus', focus)
      element.removeEventListener('blur', blur)
      element.removeEventListener('pointerdown', pointerdown)
      element.removeEventListener('keydown', keydown)
    }
  }, [])

  const status: ElementState = state.active
    ? 'active'
    : state.focus
      ? 'focus'
      : state.hover
        ? 'hover'
        : 'initial'

  return [setRef, status]
}

function resolveClassName({
  className,
  textSize = 'medium'
}: Pick<LinkProps, 'className' | 'textSize'>) {
  return classnames(
    'text-primary inline-flex cursor-pointer items-center text-left font-medium transition focus:outline-none',
    {
      'text-xl': textSize === 'medium',
      'text-lg': textSize === 'small'
    },
    className
  )
}

export default function Link({
  children,
  className,
  textSize = 'medium',
  ...props
}: LinkProps) {
  const [ref, state] = useElementState()
  const shouldReduceMotion = useReducedMotion()

  return (
    <motion.a
      {...props}
      ref={ref}
      animate={state}
      className={resolveClassName({ className, textSize })}
      transition={shouldReduceMotion ? { duration: 0 } : {}}
    >
      {children}
    </motion.a>
  )
}
